import Vector from "victor";
import { RenderConfig } from "@/store/modules/toolConfig";
import Rect from "@/wbcontroller/types/Rect";
import { Shape2 } from "@/wbcontroller/types/Shape";
import { shapeStrokeWidthScale } from "@/wbcontroller/util";
import {
  DiamondShape2,
  EllipseShape2,
  LineShape2,
  RectShape2,
} from "./StandardShapes";

export interface StandardShapeRenderer {
  draw(ctx: CanvasRenderingContext2D, shape: Shape2): void;
}

export class DummyShapeRenderer implements StandardShapeRenderer {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  draw(ctx: CanvasRenderingContext2D, shape: Shape2) {
    console.warn("I'm the dummy renderer!");
  }
}

export class LineRenderer implements StandardShapeRenderer {
  constructor(private renderConfig: RenderConfig) {}

  draw(ctx: CanvasRenderingContext2D, shape: Shape2) {
    const lineShape = shape as LineShape2;
    ctx.lineJoin = "round";
    ctx.strokeStyle = lineShape.strokeColor;
    ctx.lineWidth = lineShape.strokeWidth * shapeStrokeWidthScale(lineShape);

    // align to pixel grid
    const offset =
      lineShape.strokeWidth % 2 == 0 ? new Vector(0, 0) : new Vector(0.5, 0.5);

    const a = Vector.fromArray(lineShape.pointA).add(offset);
    const b = Vector.fromArray(lineShape.pointB).add(offset);
    const len = a.distance(b);

    ctx.save();

    // Drawing lines using transforms makes the math for cap shapes much easier
    ctx.translate(a.x, a.y);
    ctx.rotate(Math.atan2(b.y - a.y, b.x - a.x));

    const arrowLength = lineShape.strokeWidth * 2;
    switch (lineShape.lineCapB) {
      case "round":
        ctx.lineCap = this.renderConfig.defaultLineCap;
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(len, 0);
        ctx.stroke();

        break;
      case "arrow":
        ctx.lineCap = this.renderConfig.defaultLineCap;
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(len - arrowLength, 0);
        ctx.stroke();

        ctx.fillStyle = lineShape.strokeColor;
        ctx.lineWidth = 0;
        ctx.beginPath();
        ctx.moveTo(len, 0);
        ctx.lineTo(len - lineShape.strokeWidth * 4, -lineShape.strokeWidth * 2);
        ctx.lineTo(len - lineShape.strokeWidth * 4, lineShape.strokeWidth * 2);
        ctx.closePath();
        ctx.fill();
        break;
    }
    ctx.restore();
    // TODO: also do lineCapA at the other end
  }
}

export interface CanvasShadowData {
  shadowOffsetX: number;
  shadowOffsetY: number;
  shadowColor: string;
  shadowBlur: number;
}
export class RectRenderer implements StandardShapeRenderer {
  constructor(private shadow: CanvasShadowData | null = null) {}

  draw(ctx: CanvasRenderingContext2D, shape: Shape2) {
    const rectShape = shape as RectShape2;
    if (!rectShape) return;
    // align to pixel grid
    const offset =
      rectShape.strokeWidth % 2 == 0 ? new Vector(0, 0) : new Vector(0.5, 0.5);

    const rect = new Rect(
      Vector.fromArray(rectShape.pointA),
      Vector.fromArray(rectShape.pointB)
    );
    rect.min.add(offset);

    if (this.shadow) {
      ctx.shadowOffsetX = this.shadow.shadowOffsetX;
      ctx.shadowOffsetY = this.shadow.shadowOffsetY;
      ctx.shadowColor = this.shadow.shadowColor;
      ctx.shadowBlur = this.shadow.shadowBlur;
    }

    if (rectShape.fillColor !== "rgba(0, 0, 0, 0)") {
      ctx.fillStyle = rectShape.fillColor;
      ctx.fillRect(rect.minX, rect.minY, rect.width, rect.height);
    }

    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
    ctx.shadowBlur = 0;
    ctx.shadowColor = "rgba(0,0,0,0)";

    ctx.lineJoin = "round";
    ctx.strokeStyle = rectShape.strokeColor;
    ctx.lineWidth = rectShape.strokeWidth * shapeStrokeWidthScale(rectShape);

    if (ctx.lineWidth > 0) {
      ctx.beginPath();

      ctx.moveTo(rect.min.x, rect.min.y);
      ctx.lineTo(rect.maxX, rect.minY);
      ctx.lineTo(rect.maxX, rect.maxY);
      ctx.lineTo(rect.minX, rect.maxY);
      ctx.closePath();
      ctx.stroke();
      ctx.closePath();
    }
  }
}

export class DiamondRenderer implements StandardShapeRenderer {
  draw(ctx: CanvasRenderingContext2D, shape: Shape2) {
    const diamondShape = shape as DiamondShape2;
    if (!diamondShape) return;
    // align to pixel grid
    const offset =
      diamondShape.strokeWidth % 2 == 0
        ? new Vector(0, 0)
        : new Vector(0.5, 0.5);

    const rect = new Rect(
      Vector.fromArray(diamondShape.pointA),
      Vector.fromArray(diamondShape.pointB)
    );
    rect.min.add(offset);

    if (diamondShape.fillColor !== "rgba(0, 0, 0, 0)") {
      ctx.fillStyle = diamondShape.fillColor;

      ctx.beginPath();
      ctx.moveTo(rect.minX + rect.width / 2, rect.minY);
      ctx.lineTo(rect.maxX, rect.minY + rect.height / 2);
      ctx.lineTo(rect.minX + rect.width / 2, rect.maxY);
      ctx.lineTo(rect.minX, rect.minY + rect.height / 2);
      ctx.closePath();
      ctx.fill();
    }

    ctx.lineJoin = "round";
    ctx.strokeStyle = diamondShape.strokeColor;
    ctx.lineWidth =
      diamondShape.strokeWidth * shapeStrokeWidthScale(diamondShape);
    ctx.beginPath();

    ctx.moveTo(rect.minX + rect.width / 2, rect.minY);
    ctx.lineTo(rect.maxX, rect.minY + rect.height / 2);
    ctx.lineTo(rect.minX + rect.width / 2, rect.maxY);
    ctx.lineTo(rect.minX, rect.minY + rect.height / 2);

    ctx.closePath();
    ctx.stroke();
    ctx.closePath();
  }
}

export class EllipseRenderer implements StandardShapeRenderer {
  draw(ctx: CanvasRenderingContext2D, shape: Shape2) {
    const ellipseShape = shape as EllipseShape2;
    if (!ellipseShape) return;
    // align to pixel grid
    const offset =
      ellipseShape.strokeWidth % 2 == 0
        ? new Vector(0, 0)
        : new Vector(0.5, 0.5);

    const rect = new Rect(
      Vector.fromArray(ellipseShape.pointA),
      Vector.fromArray(ellipseShape.pointB)
    );
    rect.min.add(offset);

    if (ellipseShape.fillColor !== "rgba(0, 0, 0, 0)") {
      ctx.fillStyle = ellipseShape.fillColor;

      ctx.beginPath();
      ctx.ellipse(
        rect.centerX,
        rect.centerY,
        Math.abs(rect.width / 2),
        Math.abs(rect.height / 2),
        0,
        0,
        Math.PI * 2
      );
      ctx.fill();
    }

    ctx.lineJoin = "round";
    ctx.strokeStyle = ellipseShape.strokeColor;
    ctx.lineWidth =
      ellipseShape.strokeWidth * shapeStrokeWidthScale(ellipseShape);
    ctx.beginPath();

    ctx.ellipse(
      rect.centerX,
      rect.centerY,
      Math.abs(rect.width / 2),
      Math.abs(rect.height / 2),
      0,
      0,
      Math.PI * 2
    );

    ctx.closePath();
    ctx.stroke();
    ctx.closePath();
  }
}
